// ============================================================================
// SeriesPicker — small dropdown to pick a series + sequence_number for an event.
// ============================================================================
// Sits inside EventForm. When a series is picked, the event becomes a member
// of that series via cal_core_series_entry. Sequence number and instance
// label drive the "Day 3 of 5" / "Session 2" display on the detail page.
// ============================================================================

import { useEffect, useState } from 'react';
import { Settings2, RefreshCw } from 'lucide-react';
import { api, type Series } from '../api';
import { SeriesManageModal } from './SeriesManageModal';

interface Props {
  token: string;
  seriesId: number | null;
  sequenceNumber: number | null;
  instanceLabel: string;
  onChange: (next: { seriesId: number | null; sequenceNumber: number | null; instanceLabel: string }) => void;
  disabled?: boolean;
}

export function SeriesPicker({
  token, seriesId, sequenceNumber, instanceLabel, onChange, disabled,
}: Props) {
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manageOpen, setManageOpen] = useState(false);

  const load = () => {
    setLoading(true); setError('');
    api.series()
      .then((d) => setSeries(d.series ?? []))
      .catch((err) => setError(err?.message || 'Could not load series.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Series (optional)</span>
        <div className="flex gap-2">
          <button type="button" onClick={load} disabled={loading || disabled} title="Refresh"
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-white disabled:opacity-50">
            <RefreshCw className={'h-3 w-3 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <button type="button" onClick={() => setManageOpen(true)} disabled={disabled}
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-white disabled:opacity-50">
            <Settings2 className="h-3 w-3" />
            Manage library
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1 text-xs">{error}</div>}

      <label className="block">
        <span className="block text-xs font-medium text-gray-600 mb-1">Part of series</span>
        <select
          value={seriesId ?? ''}
          onChange={(e) => onChange({
            seriesId: e.target.value ? Number(e.target.value) : null,
            sequenceNumber, instanceLabel,
          })}
          disabled={disabled}
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
          <option value="">— Not part of a series —</option>
          {series.map((s) => (
            <option key={s.series_id} value={s.series_id}>
              {s.series_label} ({s.series_type})
            </option>
          ))}
        </select>
      </label>

      {seriesId && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Sequence number</span>
            <input type="number" min={0} max={9999} value={sequenceNumber ?? ''}
              onChange={(e) => onChange({
                seriesId,
                sequenceNumber: e.target.value === '' ? null : Number(e.target.value),
                instanceLabel,
              })}
              disabled={disabled}
              placeholder="e.g. 1, 2, 3…"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
            <p className="text-[11px] text-gray-500 mt-0.5">Position in the series.</p>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Instance label</span>
            <input type="text" value={instanceLabel}
              onChange={(e) => onChange({ seriesId, sequenceNumber, instanceLabel: e.target.value })}
              disabled={disabled}
              placeholder="e.g. Day 1, Session 2 of 5"
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
            <p className="text-[11px] text-gray-500 mt-0.5">Display label on detail page.</p>
          </label>
        </div>
      )}

      <SeriesManageModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        token={token}
        series={series}
        onRefresh={load}
      />
    </div>
  );
}
